import { PathStyleExtension } from "@deck.gl/extensions";
import { IconLayer, PathLayer, PathLayerProps } from "@deck.gl/layers";
import { Position } from "geojson";
import { MapLayerMouseEvent } from "maplibre-gl";
import { useMemo, useState } from "react";

import { XYLocation } from "@nens/deck-gl";

import { COLORS } from "@/config/deckgl-conts";

import { DrawLinePointPolygonProps } from "../types/draw-line-point-polygon-props";
import { DEFAULT_ICON_LAYER_PROPS, DEFAULT_PATH_LAYER_PROPS } from "../utils/map-draw-consts";

export interface DrawLineLayersProps {
  confirmDrawingCallback: () => void;
  removeLastPointCallback: () => void;
  points: XYLocation[];
  mouseLocation: XYLocation;
}

const getDrawLineLayers = ({
  points = [],
  confirmDrawingCallback,
  removeLastPointCallback,
  mouseLocation,
}: DrawLineLayersProps) => {
  if (!points.length) return [];

  const dashLine =
    mouseLocation && points.length > 0 ? [points[points.length - 1], mouseLocation] : null;

  return [
    new PathLayer({
      ...DEFAULT_PATH_LAYER_PROPS,
      id: "LINE-path-line-layer",
      data: [points],
      getColor: () => COLORS.BLUE,
    } as PathLayerProps),
    new PathLayer({
      ...DEFAULT_PATH_LAYER_PROPS,
      id: "LINE-dash-line-layer",
      data: dashLine ? [dashLine] : [],
      getColor: COLORS.BLUE,
      getDashArray: [10, 10],
      dashJustified: true,
      extensions: [new PathStyleExtension({ dash: true })],
    } as PathLayerProps),
    new IconLayer({
      ...DEFAULT_ICON_LAYER_PROPS,
      id: "LINE-confirm-line-layer",
      data: [points[points.length - 1]],
      getIcon: () => "confirm",
      onClick: () => {
        confirmDrawingCallback();
      },
      visible: points.length > 1,
    }),
    new IconLayer({
      ...DEFAULT_ICON_LAYER_PROPS,
      id: "LINE-cancel-line-layer",
      data: [points[points.length - 1]],
      getIcon: () => (points.length > 1 ? "cancel" : "cancelNoAnchor"),
      onClick: () => {
        removeLastPointCallback();
        return true;
      },
    }),
  ];
};

export const useDrawLine = ({
  active,
  setRemovePopup,
  mouseLocation,
  isDeleting,
  acceptedGeometryTypes,
  formData,
  onChange,
}: DrawLinePointPolygonProps) => {
  const lines: Position[][] = useMemo(() => {
    if (formData) {
      if (formData.type === "LineString") return [formData.coordinates];
      if (formData.type === "MultiLineString") return formData.coordinates;
    }
    return [];
  }, [formData]);
  const [drawingPoints, setDrawingPoints] = useState<XYLocation[]>([]);

  if (!active && drawingPoints.length) setDrawingPoints([]);

  const onMapClick = (e: MapLayerMouseEvent) => {
    const mouseLocation = e.lngLat.toArray();
    if (!active || isDeleting) return;
    if (!acceptedGeometryTypes.includes("MultiLineString") && lines.length) {
      onChange({ type: "LineString", coordinates: [] });
    }
    setDrawingPoints([...(drawingPoints ?? []), mouseLocation]);
  };

  const confirmDrawingCallback = () => {
    if (!drawingPoints) return;
    setDrawingPoints([]);

    // If no formData, start with a LineString
    if (!formData) {
      onChange({
        type: "LineString",
        coordinates: drawingPoints,
      });
      return;
    }

    // If formData is a LineString, convert to MultiLineString unless it was empty
    if (formData.type === "LineString") {
      if (!formData.coordinates.length) {
        onChange({
          type: "LineString",
          coordinates: drawingPoints,
        });
        return;
      }

      onChange({
        type: "MultiLineString",
        coordinates: [formData.coordinates, drawingPoints],
      });
      return;
    }

    // If formData is a MultiLineString, add new LineString
    if (formData.type === "MultiLineString") {
      onChange({
        type: "MultiLineString",
        coordinates: [...formData.coordinates, drawingPoints],
      });
      return;
    }
  };

  const removeLastPointCallback = () => {
    setDrawingPoints(drawingPoints.slice(0, -1));
  };

  const newLineLayers = getDrawLineLayers({
    points: drawingPoints,
    confirmDrawingCallback,
    removeLastPointCallback,
    mouseLocation,
  });

  const drawnLines = new PathLayer({
    ...DEFAULT_PATH_LAYER_PROPS,
    id: "PATH-existing-path-layer",
    pickable: isDeleting,
    onClick: ({ index }) => {
      setRemovePopup({
        coordinates: mouseLocation,
        type: "Line",
        onConfirm: () => {
          if (!formData) return;
          if (formData.type === "LineString") {
            onChange({ type: "LineString", coordinates: [] });
            return;
          }
          if (formData.type === "MultiLineString") {
            const linesCopy = [...lines];
            linesCopy.splice(index, 1);
            if (linesCopy.length > 1) {
              onChange({
                type: "MultiLineString",
                coordinates: linesCopy,
              });
              return;
            }
            onChange({ type: "LineString", coordinates: linesCopy[0] });
          }
        },
      });
    },
    data: lines,
  });

  return {
    lineLayer: [drawnLines, ...newLineLayers],
    lines,
    onMapClick,
  };
};
